import React, { useState } from 'react';
import { Check, Eye, EyeOff, Loader2, Lock, LogOut, ShieldAlert, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

import { db } from '../services/db';
import type { ClientEntity } from '../types';

/**
 * ============================================================================
 * CONTA E SEGURANCA — a tela onde o cliente troca a propria senha
 * ============================================================================
 *
 * Ela nao existia. O cliente recebia uma senha do operador e nao tinha como
 * mudar: quem entregou continuava sabendo entrar, para sempre. Com a senha
 * padrao entrando em cena, isso deixa de ser incomodo e vira problema — uma
 * senha de fabrica que ninguem troca e uma senha publicada.
 *
 * A troca exige a senha ATUAL de proposito. Sem isso, uma aba esquecida num
 * computador emprestado viraria posse permanente da conta em um clique, e o
 * dono nao teria como retomar. A regra e conferida no servidor; o formulario so
 * evita a ida a toa.
 */

interface Props {
  client: ClientEntity;
  onLogout: () => void;
  /** Cor da marca do cliente, para a tela nao destoar do resto do painel. */
  corPrimaria: string;
  /** Ainda esta com a senha de fabrica? Vem de quem contem, para o aviso sumir. */
  precisaTrocarSenha: boolean;
  onSenhaTrocada: () => void;
}

export const ContaDoCliente: React.FC<Props> = ({
  client,
  onLogout,
  corPrimaria,
  precisaTrocarSenha,
  onSenhaTrocada,
}) => {
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [trocada, setTrocada] = useState(false);

  const precisaTrocar = !trocada && precisaTrocarSenha;

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);

    if (novaSenha.length < 8) {
      setErro('A nova senha precisa de pelo menos 8 caracteres.');
      return;
    }
    if (novaSenha !== confirmacao) {
      // Conferido aqui porque o servidor nem recebe a confirmacao: ela existe
      // para pegar erro de digitacao, nao para autorizar nada.
      setErro('A confirmacao nao bate com a nova senha.');
      return;
    }
    if (novaSenha === senhaAtual) {
      setErro('A nova senha precisa ser diferente da atual.');
      return;
    }

    setSalvando(true);
    try {
      const recusa = await db.changePortalPassword(senhaAtual, novaSenha);
      if (recusa) {
        // Recusa do servidor (senha atual errada, nova igual a padrao). E uma
        // resposta legitima, nao uma falha: mostra o que ele disse.
        setErro(recusa);
        return;
      }
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmacao('');
      setTrocada(true);
      // Avisa quem contem para o ponto de alerta na navegacao sumir junto. Sem
      // isso o painel continuaria cobrando uma troca que acabou de acontecer.
      onSenhaTrocada();
      toast.success('Senha trocada. As outras sessoes foram encerradas.');
    } catch (e) {
      console.error('[conta] falha ao trocar a senha:', e);
      setErro('Nao consegui falar com o servidor. Tente de novo em instantes.');
    } finally {
      setSalvando(false);
    }
  };

  const campo =
    'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black/10 transition-shadow';

  return (
    <div className="p-6 md:p-10 max-w-2xl w-full mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900 tracking-tight">Conta e Seguranca</h1>
        <p className="text-sm text-gray-500 mt-1">
          Quem entra neste painel e o acesso de <b>{client.data.name}</b>.
        </p>
      </div>

      {precisaTrocar && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <ShieldAlert size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <h2 className="text-sm font-semibold text-amber-900">
              Voce ainda esta com a senha padrao
            </h2>
            <p className="text-xs text-amber-800 mt-1">
              Ela foi entregue pela equipe e e a mesma para todo mundo que ainda nao trocou.
              Escolha uma sua agora — enquanto isso nao acontecer, quem conhece o padrao
              consegue entrar aqui.
            </p>
          </div>
        </div>
      )}

      {trocada && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
          <ShieldCheck size={18} className="text-green-600 shrink-0 mt-0.5" />
          <div>
            <h2 className="text-sm font-semibold text-green-900">Senha trocada</h2>
            <p className="text-xs text-green-800 mt-1">
              As outras sessoes abertas com a senha antiga foram encerradas. Esta continua
              valendo.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-5 sm:p-6 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <Lock size={15} /> Trocar minha senha
        </h2>
        <p className="text-xs text-gray-500 mb-5">
          Precisamos da senha atual: e o que impede alguem que pegou esta tela aberta de
          tomar a conta.
        </p>

        <form onSubmit={enviar} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1.5">Senha atual</label>
            <input
              type={mostrar ? 'text' : 'password'}
              value={senhaAtual}
              onChange={(e) => setSenhaAtual(e.target.value)}
              autoComplete="current-password"
              required
              className={campo}
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1.5">Nova senha</label>
            <input
              type={mostrar ? 'text' : 'password'}
              value={novaSenha}
              onChange={(e) => setNovaSenha(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              maxLength={72}
              required
              className={campo}
            />
            <p className="text-[11px] text-gray-400 mt-1">Pelo menos 8 caracteres.</p>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1.5">
              Repita a nova senha
            </label>
            <input
              type={mostrar ? 'text' : 'password'}
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              autoComplete="new-password"
              required
              className={campo}
            />
          </div>

          <button
            type="button"
            onClick={() => setMostrar((v) => !v)}
            className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-900"
          >
            {mostrar ? <EyeOff size={14} /> : <Eye size={14} />}
            {mostrar ? 'Esconder as senhas' : 'Mostrar as senhas'}
          </button>

          {erro && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {erro}
            </p>
          )}

          <button
            type="submit"
            disabled={salvando}
            className="w-full flex items-center justify-center gap-2 text-white py-2.5 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: corPrimaria }}
          >
            {salvando ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            Trocar senha
          </button>
        </form>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 sm:p-6 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900 mb-1">Sair deste painel</h2>
        <p className="text-xs text-gray-500 mb-4">
          Encerra a sessao no servidor, nao so neste navegador.
        </p>
        <button
          onClick={onLogout}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
        >
          <LogOut size={15} /> Sair do Painel
        </button>
      </div>
    </div>
  );
};
